import { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Controller } from "react-hook-form";
import {
    Breadcrumb,
    Button,
    Card,
    Col,
    Divider,
    Form,
    Input,
    Row,
    Select,
    Tag,
    Typography,
} from "antd";
import {
    ArrowLeftOutlined,
    CheckOutlined,
    EditOutlined,
    NotificationOutlined,
} from "@ant-design/icons";
import { Editor } from "react-draft-wysiwyg";
// @ts-ignore
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import { INotice } from "../../../../interfaces";
import { ThemeContext } from "../../../../contexts/ThemeContext";
import useNoticeForm from "./useNoticeForm";
import PhoneMockupHome from "../PhoneMockupHome";
import PageHeader from "../../../../components/PageHeader";

const { Text } = Typography;

interface IProps {
    action: "Adicionar" | "Editar";
    notice?: INotice;
    onSubmit: (notice: INotice) => Promise<void>;
}

export default function NoticeForm({ action, notice, onSubmit }: IProps) {
    const {
        handleSubmitForm,
        handleSubmit,
        control,
        isSubmiting,
        errors,
        defaultEditorState,
        editorState,
        onChangeEditor,
        content,
        title,
    } = useNoticeForm(onSubmit, notice);

    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const navigate = useNavigate();

    const isEdit = action === "Editar";

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Breadcrumb Navigation Card */}
            <div
                style={{
                    background: isDark ? "#141414" : "#ffffff",
                    border: `1px solid ${isDark ? "#262626" : "#f0f0f0"}`,
                    padding: "10px 16px",
                    borderRadius: 8,
                }}
            >
                <Breadcrumb
                    items={[
                        {
                            title: <Link to="/avisos">Avisos e Notícias</Link>,
                        },
                        {
                            title: isEdit ? `Editar Aviso #${notice?.id ?? "--"}` : "Novo Aviso",
                        },
                    ]}
                />
            </div>

            {/* Page Header */}
            <PageHeader
                icon={<NotificationOutlined />}
                iconBg="linear-gradient(135deg, #fa8c16 0%, #fa541c 100%)"
                title={isEdit ? `Editar Aviso: ${notice?.title || ""}` : "Novo Aviso ou Notícia"}
                tag={
                    isEdit && notice?.id ? (
                        <Tag color={notice.status ? "success" : "error"}>
                            {notice.status ? "Ativo" : "Inativo"} (ID #{notice.id})
                        </Tag>
                    ) : undefined
                }
                subtitle={
                    isEdit
                        ? "Atualize as informações, o título e o conteúdo formatado deste comunicado"
                        : "Cadastre um novo comunicado ou notícia e visualize a prévia em tempo real de como será exibido no aplicativo"
                }
                actions={
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate("/avisos")}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontWeight: 500,
                            height: 38,
                            borderRadius: 8,
                        }}
                    >
                        Voltar para Avisos
                    </Button>
                }
            />

            {/* Form Container Card */}
            <Card
                style={{
                    borderRadius: 12,
                    border: `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                    background: isDark ? "#141414" : "#ffffff",
                    boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.03)",
                }}
                bodyStyle={{ padding: "20px 24px" }}
            >
                <Form
                    onFinish={handleSubmit(handleSubmitForm)}
                    autoComplete="off"
                    layout="vertical"
                >
                    <Row gutter={[24, 24]}>
                        {/* Left Column: Form Fields */}
                        <Col xs={24} lg={15} xl={16}>
                            {/* Section 1: Identificação do Aviso */}
                            <div style={{ marginBottom: 20 }}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        marginBottom: 16,
                                        paddingBottom: 10,
                                        borderBottom: `1px solid ${isDark ? "#262626" : "#eaedf1"}`,
                                    }}
                                >
                                    <NotificationOutlined style={{ color: "#fa8c16", fontSize: 18 }} />
                                    <div>
                                        <Text strong style={{ fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                            Identificação do Comunicado
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: 11.5 }}>
                                            Defina o título principal e o status de exibição no aplicativo
                                        </Text>
                                    </div>
                                </div>

                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={16} md={18}>
                                        <Form.Item
                                            label={<strong>Título do Aviso</strong>}
                                            required
                                            validateStatus={errors.title ? "error" : undefined}
                                            help={errors.title?.message}
                                        >
                                            <Controller
                                                name="title"
                                                control={control}
                                                rules={{
                                                    required: "Por favor, digite o título do aviso!",
                                                    maxLength: {
                                                        value: 100,
                                                        message: "O título precisa ter no máximo 100 caracteres.",
                                                    },
                                                }}
                                                render={({ field }) => (
                                                    <Input
                                                        {...field}
                                                        placeholder="Ex: Novo Sorteio de Natal Disponível!"
                                                        size="large"
                                                        style={{ borderRadius: 8 }}
                                                        showCount
                                                        maxLength={100}
                                                        prefix={<NotificationOutlined style={{ color: "#fa8c16" }} />}
                                                    />
                                                )}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={8} md={6}>
                                        <Form.Item
                                            label={<strong>Status</strong>}
                                            required
                                            validateStatus={errors.status ? "error" : undefined}
                                            help={errors.status?.message}
                                        >
                                            <Controller
                                                name="status"
                                                control={control}
                                                rules={{
                                                    required: "Por favor, selecione o status!",
                                                }}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        size="large"
                                                        style={{ width: "100%", borderRadius: 8 }}
                                                        value={String(field.value)}
                                                        onChange={(val) => field.onChange(val)}
                                                        options={[
                                                            { value: "true", label: "Ativo" },
                                                            { value: "false", label: "Inativo" },
                                                        ]}
                                                    />
                                                )}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            {/* Section 2: Conteúdo da Mensagem */}
                            <div style={{ marginBottom: 20 }}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        marginBottom: 16,
                                        paddingBottom: 10,
                                        borderBottom: `1px solid ${isDark ? "#262626" : "#eaedf1"}`,
                                    }}
                                >
                                    <EditOutlined style={{ color: "#1677ff", fontSize: 18 }} />
                                    <div>
                                        <Text strong style={{ fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                            Conteúdo do Comunicado
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: 11.5 }}>
                                            Escreva o texto com formatações, links e cores que serão renderizados no aplicativo
                                        </Text>
                                    </div>
                                </div>

                                <Form.Item
                                    label={<strong>Texto / Mensagem</strong>}
                                    required
                                    validateStatus={errors.content ? "error" : undefined}
                                    help={errors.content?.message}
                                >
                                    <div
                                        style={{
                                            border: `1px solid ${
                                                errors.content?.message
                                                    ? "#ff4d4f"
                                                    : isDark
                                                    ? "#303030"
                                                    : "#d9d9d9"
                                            }`,
                                            borderRadius: 8,
                                            overflow: "hidden",
                                            background: isDark ? "#1f1f1f" : "#ffffff",
                                        }}
                                    >
                                        <Editor
                                            localization={{
                                                translations: {
                                                    "generic.add": "Adicionar",
                                                    "generic.cancel": "Cancelar",
                                                    "components.controls.colorpicker.colorpicker": "Cor",
                                                    "components.controls.colorpicker.text": "Texto",
                                                    "components.controls.colorpicker.background": "Fundo",
                                                    "components.controls.emoji.emoji": "Emoji",
                                                    "components.controls.fontfamily.fontfamily": "Fonte",
                                                    "components.controls.fontsize.fontsize": "Tamanho",
                                                    "components.controls.history.history": "Histórico",
                                                    "components.controls.history.undo": "Desfazer",
                                                    "components.controls.history.redo": "Refazer",
                                                    "components.controls.inline.bold": "Negrito",
                                                    "components.controls.inline.italic": "Itálico",
                                                    "components.controls.link.linkTitle": "Título",
                                                    "components.controls.link.linkTarget": "Link",
                                                    "components.controls.link.linkTargetOption": "Nova janela",
                                                    "components.controls.link.link": "Link",
                                                    "components.controls.link.unlink": "Remover Link",
                                                    "components.controls.list.list": "Lista",
                                                    "components.controls.list.unordered": "Marcadores",
                                                    "components.controls.list.ordered": "Numerada",
                                                    "components.controls.textalign.textalign": "Alinhamento",
                                                    "components.controls.textalign.left": "Esquerda",
                                                    "components.controls.textalign.center": "Centro",
                                                    "components.controls.textalign.right": "Direita",
                                                    "components.controls.textalign.justify": "Justificar",
                                                },
                                            }}
                                            editorState={editorState}
                                            defaultEditorState={defaultEditorState}
                                            toolbarStyle={{
                                                backgroundColor: isDark ? "#141414" : "#f8fafc",
                                                borderBottom: `1px solid ${isDark ? "#303030" : "#eaedf1"}`,
                                                margin: 0,
                                                padding: "6px 8px",
                                            }}
                                            editorStyle={{
                                                minHeight: 180,
                                                maxHeight: 280,
                                                padding: "10px 14px",
                                                color: isDark ? "#f0f0f0" : "#1e293b",
                                                backgroundColor: isDark ? "#1f1f1f" : "#ffffff",
                                            }}
                                            onEditorStateChange={onChangeEditor}
                                            toolbar={{
                                                options: [
                                                    "inline",
                                                    "fontSize",
                                                    "list",
                                                    "textAlign",
                                                    "colorPicker",
                                                    "emoji",
                                                    "link",
                                                    "history",
                                                ],
                                                inline: {
                                                    options: ["bold", "italic", "underline"],
                                                },
                                                list: {
                                                    options: ["unordered", "ordered"],
                                                },
                                                textAlign: {
                                                    options: ["left", "center", "right", "justify"],
                                                },
                                            }}
                                        />
                                    </div>
                                </Form.Item>
                            </div>

                            <Divider style={{ margin: "20px 0 16px 0" }} />

                            {/* Action Footer */}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
                                <Button
                                    onClick={() => navigate("/avisos")}
                                    disabled={isSubmiting}
                                    style={{
                                        height: 38,
                                        borderRadius: 8,
                                        fontWeight: 500,
                                    }}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<CheckOutlined />}
                                    loading={isSubmiting}
                                    disabled={isSubmiting}
                                    style={{
                                        height: 38,
                                        borderRadius: 8,
                                        fontWeight: 600,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 6,
                                    }}
                                >
                                    {isEdit ? "Salvar Alterações" : "Cadastrar Aviso"}
                                </Button>
                            </div>
                        </Col>

                        {/* Right Column: Mobile Live Preview */}
                        <Col
                            xs={24}
                            lg={9}
                            xl={8}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                paddingLeft: 8,
                            }}
                        >
                            <div style={{ marginBottom: 12, textAlign: "center" }}>
                                <Text strong style={{ fontSize: 13, color: isDark ? "#d4d4d4" : "#475569" }}>
                                    Pré-visualização no Aplicativo
                                </Text>
                                <div style={{ fontSize: 11.5, color: isDark ? "#8c8c8c" : "#94a3b8" }}>
                                    Visualização em tempo real da tela inicial do cidadão
                                </div>
                            </div>
                            <div style={{ transform: "scale(0.92)", transformOrigin: "top center" }}>
                                <PhoneMockupHome
                                    darkMode={isDark}
                                    content={content}
                                    title={title}
                                />
                            </div>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
}
